import { createSlice, isAnyOf } from '@reduxjs/toolkit'
import { getCustomerChannelsAsync, getCustomerReportAsync, getCustomersAsync, getSubscriptionTimelineAsync } from '../actions/customerAction'
import { CUSTOMER, ReportData, SubscriptionTimeline, VENDOR_USER } from 'src/app/_interfaces/customer.interface'

type CustomerChannelOption = {
  value: string
  label: string
}

export type initialState = {
  customerLoader: boolean
  totalCustomer: number
  customerList: CUSTOMER[] | []
  channelFilterOptions: CustomerChannelOption[]
  channelFilterLoader: boolean
  reportLoader: boolean
  reportData: ReportData[]
  selectedCustomerForReport: CUSTOMER | null
  selectedVendorData: VENDOR_USER | null
  timelineLoading: boolean
  timelineData: SubscriptionTimeline | null
}

const initialState: initialState = {
  customerLoader: false,
  totalCustomer: 0,
  customerList: [],
  channelFilterOptions: [],
  channelFilterLoader: false,
  reportLoader: false,
  reportData: [],
  selectedCustomerForReport: null,
  selectedVendorData: null,
  timelineLoading: false,
  timelineData: null,
}

const formatChannelLabel = (channelCode: string) =>
  channelCode
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()

const extractListFromPayload = (payload: any): any[] => {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.data)) return payload.data
  if (Array.isArray(payload?.results)) return payload.results
  if (Array.isArray(payload?.connected_platforms)) return payload.connected_platforms
  if (Array.isArray(payload?.platforms)) return payload.platforms
  return []
}

const resolveChannelCodeFromPlatform = (platform: any): string => {
  const raw =
    platform?.name ??
    platform?.code ??
    platform?.platform_info?.code ??
    platform?.platform_info?.name ??
    platform?.display_name ??
    platform?.platform_info?.display_name ??
    ''
  return String(raw).trim()
}

const resolveChannelLabelFromPlatform = (platform: any, channelCode: string): string => {
  const rawLabel =
    platform?.display_name ??
    platform?.platform_info?.display_name ??
    platform?.name ??
    platform?.platform_info?.name ??
    channelCode
  const normalizedLabel = String(rawLabel ?? '').trim()
  return normalizedLabel || formatChannelLabel(channelCode)
}

const mergeChannelOptions = (
  existingOptions: CustomerChannelOption[],
  incomingOptions: CustomerChannelOption[]
) => {
  const channelMap = new Map<string, string>()

  existingOptions.forEach((item) => {
    if (!item?.value) {
      return
    }
    channelMap.set(item.value, item.label || item.value)
  })

  incomingOptions.forEach((item) => {
    if (!item?.value) {
      return
    }
    if (!channelMap.has(item.value)) {
      channelMap.set(item.value, item.label || item.value)
    }
  })

  return Array.from(channelMap.entries())
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label))
}

const customerSlice = createSlice({
  name: 'customerSlice',
  initialState,
  extraReducers: (builder) => {
    //get customer list .....
    builder.addMatcher(isAnyOf(getCustomersAsync.pending), (state) => {
      state.customerLoader = true
    })
    builder.addMatcher(isAnyOf(getCustomersAsync.rejected), (state) => {
      state.customerLoader = false
    })
    builder.addMatcher(isAnyOf(getCustomersAsync.fulfilled), (state, action) => {
      state.customerList = action.payload?.data?.data || []
      state.totalCustomer = action.payload?.data?.total
      const channelsFromUsers: CustomerChannelOption[] = (action.payload?.data?.data || [])
        .map((customer: CUSTOMER) => {
          const channelCode = String(customer?.source ?? '').trim()
          if (!channelCode) {
            return null
          }
          return {
            value: channelCode,
            label: formatChannelLabel(channelCode),
          }
        })
        .filter((item: CustomerChannelOption | null): item is CustomerChannelOption => item !== null)

      state.channelFilterOptions = mergeChannelOptions(state.channelFilterOptions, channelsFromUsers)
      state.customerLoader = false
    })
    //get customer list end .....

    //get customer channel options .....
    builder.addMatcher(isAnyOf(getCustomerChannelsAsync.pending), (state) => {
      state.channelFilterLoader = true
    })
    builder.addMatcher(isAnyOf(getCustomerChannelsAsync.rejected), (state) => {
      state.channelFilterLoader = false
    })
    builder.addMatcher(isAnyOf(getCustomerChannelsAsync.fulfilled), (state, action) => {
      const channelsFromPlatforms: CustomerChannelOption[] = extractListFromPayload(action.payload)
        .map((platform: any) => {
          const channelCode = resolveChannelCodeFromPlatform(platform)
          if (!channelCode) {
            return null
          }
          const label = resolveChannelLabelFromPlatform(platform, channelCode)
          return {
            value: channelCode,
            label,
          }
        })
        .filter((item: CustomerChannelOption | null): item is CustomerChannelOption => item !== null)

      state.channelFilterOptions = mergeChannelOptions(state.channelFilterOptions, channelsFromPlatforms)
      state.channelFilterLoader = false
    })
    //get customer channel options end .....

    // get report data of customer .....
    builder.addMatcher(isAnyOf(getCustomerReportAsync.pending), (state) => {
      state.reportLoader = true
    })
    builder.addMatcher(isAnyOf(getCustomerReportAsync.rejected), (state) => {
      state.reportLoader = false
    })
    builder.addMatcher(isAnyOf(getCustomerReportAsync.fulfilled), (state, action) => {
      if (action.payload.data?.platform_data) {
        state.reportData = action.payload.data?.platform_data
      } else {
        state.reportData = []
      }
      state.reportLoader = false
      console.log('customer report got successfully=====> ', action.payload.data?.platform_data);

    })
    // get report data of customer end .....

    // subscription timeline
    builder.addMatcher(isAnyOf(getSubscriptionTimelineAsync.pending), (state) => {
      state.timelineLoading = true
      state.timelineData = null
    })
    builder.addMatcher(isAnyOf(getSubscriptionTimelineAsync.rejected), (state) => {
      state.timelineLoading = false
    })
    builder.addMatcher(isAnyOf(getSubscriptionTimelineAsync.fulfilled), (state, action) => {
      state.timelineLoading = false
      state.timelineData = action.payload?.data ?? null
    })

  },
  reducers: {
    setSelectedCustomerForReport: (state, action: { payload: CUSTOMER | null }) => {
      state.selectedCustomerForReport = action.payload
    },
    setVendorDataForUpdate: (state, action: { payload: VENDOR_USER | null }) => {
      state.selectedVendorData = action.payload
    },
    clearTimelineData: (state) => {
      state.timelineData = null
      state.timelineLoading = false
    },
  },
})

export const { setSelectedCustomerForReport, setVendorDataForUpdate, clearTimelineData } = customerSlice.actions
export default customerSlice.reducer
